import React from 'react';
import { Tabs, TabPane, Badge, Text } from 'components';
import { useBreakpoint } from 'hooks';
import styled from 'styled-components';
import CalendarSyncProviderModal from '@app/DashboardComponents/CalendarSyncProviderModal';
import { AlmanacCalendarProviderType } from '@bb/domain/dist/types';
import { featureEnabled } from 'utils';
import { Color } from '@bb/elements-web-rebranding';
import {
  AgentCalendarMenu,
  ListingCalendarMenu,
  ShowingCalendarMenuOption,
  TabPaneType,
} from './types';
import { useShowingsCalendarContext } from './ShowingsCalendarContext';
import OuterTable from './Tables/OuterTable';
import ToolBar from './ShowingToolBar';
import MobileFilterHeader from './ShowingToolBar/MobileFilterHeader';
import SyncByListingModal from './SyncByListingModal';
import AgentUserCalendarContext from './AgentUserCalendarContext';

const ShowingsCalendarTabs: React.FC = () => {
  const { md } = useBreakpoint();
  const context = useShowingsCalendarContext();
  const {
    selectedMenuOption,
    calendarType,
    buyerActionCount,
    listingActionCount,
    inPersonActionCount,
    setMenuOption,
    // setIsFetching,
    hasFeature,
    calendarModalVisible,
    setCalendarModalVisible,
    selectedAppointments,
    listingsToSync,
    listingId,
  } = context;

  const agentUserCalendarContext = React.useContext(AgentUserCalendarContext);

  const [isSearchClicked, setIsSearchClicked] = React.useState<boolean>(false);

  const tabPaneList: TabPaneType[] = React.useMemo(
    () =>
      calendarType === 'listing'
        ? [
            {
              key: ListingCalendarMenu.InPerson,
              label: (
                <StyledTabLabel data-cy="showings-calendar-in-person-tab">
                  {ListingCalendarMenu.InPerson}
                  <Badge
                    count={inPersonActionCount}
                    offset={[0, 1]}
                    defaultAlign
                    data-cy="in-person-count"
                  />
                </StyledTabLabel>
              ),
            },
            hasFeature('VIRTUAL_SHOWINGS') && {
              key: ListingCalendarMenu.Virtual,
              label: (
                <StyledTabLabel>{ListingCalendarMenu.Virtual}</StyledTabLabel>
              ),
            },
          ].filter(Boolean)
        : [
            {
              key: AgentCalendarMenu.Buyer,
              label: (
                <StyledTabLabel data-cy="showings-calendar-buyer-tab">
                  {AgentCalendarMenu.Buyer}
                  <Badge
                    count={buyerActionCount}
                    offset={[0, 1]}
                    defaultAlign
                    data-cy="buyer-action-count"
                  />{' '}
                </StyledTabLabel>
              ),
            },
            {
              key: AgentCalendarMenu.Listing,
              label: (
                <StyledTabLabel data-cy="showings-calendar-listing-tab">
                  {AgentCalendarMenu.Listing}
                  <Badge
                    count={listingActionCount}
                    offset={[0, 1]}
                    defaultAlign
                    data-cy="listing-action-count"
                  />{' '}
                </StyledTabLabel>
              ),
            },
          ],
    [buyerActionCount, calendarType, inPersonActionCount, listingActionCount],
  );

  return (
    <>
      <>
        <CalendarSyncProviderModal
          showModal={calendarModalVisible}
          closeModal={() => setCalendarModalVisible(false)}
          redirectUser={(service: AlmanacCalendarProviderType) => {
            agentUserCalendarContext.redirectToCalendarSyncOauth({
              service,
              listingId,
              agent: selectedMenuOption,
              appointmentIds: selectedAppointments,
              listingIds: listingsToSync,
            });
          }}
        />
        <SyncByListingModal />
      </>

      <StyledTabs
        onChange={(key: ShowingCalendarMenuOption) => {
          setMenuOption(key);
        }}
        activeKey={selectedMenuOption}
        onTabClick={(key: ShowingCalendarMenuOption) => {
          setMenuOption(key);
        }}
        $isSearchClicked={isSearchClicked}
        tabBarStyle={{ margin: 0, padding: `0 ${md ? 25 : 10}px` }}
        tabBarGutter={md ? 32 : 20}
        tabBarExtraContent={
          <ToolBar
            setIsSearchClicked={setIsSearchClicked}
            isSearchClicked={isSearchClicked}
          />
        }
        $hasSupraRebrandingFeature={featureEnabled('supraRebrandingFeature', 1)}
      >
        {tabPaneList.map(({ label, key }) => (
          <TabPane
            key={key}
            tab={<Text>{label}</Text>}
            data-cy={`showing-calendar-${label.props.children[0]}-tab`}
          >
            <MobileFilterHeader />
            <OuterTable />
          </TabPane>
        ))}
      </StyledTabs>
    </>
  );
};

const StyledTabs = styled(Tabs)<{
  $isSearchClicked: boolean;
  $hasSupraRebrandingFeature: boolean;
}>`
  .ant-tabs-nav-wrap {
    display: ${({ $isSearchClicked }) =>
      $isSearchClicked ? 'none !important' : 'flex'};
  }
  .ant-tabs-nav {
    ${({ $isSearchClicked }) => {
      if ($isSearchClicked) {
        return 'display: block';
      }
    }}
  }

  .ant-tabs-ink-bar {
    margin: 0 !important;
    ${({ $hasSupraRebrandingFeature }) =>
      $hasSupraRebrandingFeature && `background-color: ${Color.PRIMARY};`}
  }
`;

const StyledTabLabel = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 8px;
  padding: 0 16px;
`;

export default ShowingsCalendarTabs;
